import { instance } from "apis/interceptor";
import { UserRole } from "utils/role";
import { TMeetingRoom } from "./meeting-room";
import { RepeatReservationDeleteOption } from "uis/modal/repeat-reservation-delete-modal/types";
import { RepeatReservationConditionalOption } from "uis/modal/repeat-edit-conditinal-modal";

export type ResponseType<T> = {
  success: boolean;
  message?: string;
  data?: T;
};

type TCreateMeetingReservation = {
  title: string;
  memo: string;
  isPrivate: boolean;
  guestsCanModify: boolean;
  rsvStartTime: string;
  rsvEndTime: string;
  meetingRoomId: string;
  reservationAttendees: TReservationAttendee[];
  rrule?: string;
};

type TUpdateMeetingReservation = {
  id: string;
  title: string;
  memo: string;
  isPrivate: boolean;
  guestsCanModify: boolean;
  rsvStartTime: string;
  rsvEndTime: string;
  googleEventId?: string;
  beforeMeetingRoomId: string;
  afterMeetingRoomId: string;
  reservationAttendees: TReservationAttendee[];
  rrule?: string;
  editOption?: string;
  exceptedAttendeeLimit?: boolean;
};

type TDeleteMeetingReservation = {
  googleEventId?: string;
  meetingReservationId: string;
  meetingRoomId: string;
  editOption?: RepeatReservationDeleteOption;
};

type TGetMeetingReservation = {
  googleEventId?: string;
  meetingReservationId: string;
  meetingRoomId: string;
};

//TODO: 나중에 도메인에 맞게 수정 필요
export type TReservation = {
  id: string;
  title: string;
  memo: string;
  rsvStartTime: string;
  rsvEndTime: string;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  rsvStatus: "WAIT" | "NOSHOW" | "DONE" | "ONAIR";
  extendMinute: number;
  isPrivate: boolean;
  guestsCanModify: boolean;
  rsvProvider: "MILE" | "GOOGLE";
  googleEventId: string;
  instanceId?: string;
  rrule?: string;
  recurringStartDate?: string;
  reservationColor: {
    id: number;
    primary: string;
    secondary: string;
  };
  reservationCreator: {
    isVisitor: boolean;
    visitorEmail: string;
    member: {
      id: string;
      nickname: string;
      image: string;
      role: UserRole;
      accessedAt: string;
      deleteFlag: boolean;
      email: string;
    };
  } | null;
  isMyMeeting: boolean;
  reservationAttendees?: TMeetingAttendee[];
  meetingRoom: TMeetingRoom;
};

//INFO: 회의실 조회시 사용되는 참석자 내역 정보
export type TMeetingAttendee = {
  isVisitor: boolean;
  visitorEmail: string;
  member: {
    id: string;
    nickname: string;
    image: string;
    role: UserRole;
    accessedAt: string;
    deleteFlag: boolean;
    email: string;
  };
};

export type TFormattedAttendee = {
  isVisitor: boolean;
  email: string;
  picture?: string;
  id?: string | null;
  name?: string;
};

//INFO: 회의실 예약에 사용되는 참석자 정보
export type TReservationAttendee = {
  isVisitor: boolean;
  visitorEmail?: string;
  memberId?: string | null;
};

//INFO: 나의 참석/예약 리스트 Pagination 조회
export type TMyReservationPagination = {
  totalSearchNumber: number;
  totalPageNumber: number;
  nowPageNumber: number;
  meetingReservationList: TReservation[];
};

//* INFO: (new) 회의실 예약하는 api
export const createMeetingReservation = async (data: TCreateMeetingReservation) => {
  try {
    //TODO: 예외 예약은 어떻게 처리할지 확인 필요
    const res = await instance.post("/meetings/meeting-reservation", data);

    return { success: true, data: res.data.result };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "회의실을 예약할 수 있는 권한이 없습니다." };
    } else if (status === 409) {
      return { success: false, message: "해당 시간은 이미 예약되었습니다. 다른 시간을 선택해 주세요.", status: status };
    } else return { success: false, message: "회의실을 예약할 수 없습니다. 다시 시도해주세요." };
  }
};

//INFO: (new) 회의실 예약 수정하는 api
export const updateMeetingReservation = async (data: TUpdateMeetingReservation) => {
  try {
    const res = await instance.patch("/meetings/meeting-reservation", data);
    return { success: true, data: res.data.result };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "회의실을 수정할 수 있는 권한이 없습니다." };
    } else if (status === 409) {
      return { success: false, message: "해당 시간은 이미 예약되었습니다. 다른 시간을 선택해 주세요.", status: status };
    } else return { success: false, message: "회의실을 예약할 수 없습니다. 다시 시도해주세요." };
  }
};

//INFO: (new) 회의실 예약 삭제하는 api
export const deleteMeetingReservation = async (data: TDeleteMeetingReservation) => {
  let editOption =
    data.editOption === RepeatReservationDeleteOption.ALL_SCHEDULES
      ? "ALL"
      : data.editOption === RepeatReservationDeleteOption.THIS_SCHEDULE
        ? "THIS"
        : data.editOption === RepeatReservationDeleteOption.THIS_SCHEDULE_AND_FUTURE_SCHEDULES
          ? "THIS_AND_FUTURE"
          : undefined;

  try {
    const res = await instance.delete("/meetings/meeting-reservation", {
      data: {
        googleEventId: data.googleEventId ?? "",
        meetingReservationId: data.meetingReservationId,
        meetingRoomId: data.meetingRoomId,
        editOption: editOption,
      },
    });
    return { success: true, data: res.data.result };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "예약을 삭제할 수 있는 권한이 없습니다." };
    } else return { success: false, message: "예약을 삭제할 수 없습니다. 다시 시도해주세요." };
  }
};

//INFO: (new) 회의실 예약 리스트 가져오는 api
export const getMeetingReservationList = async (
  roomIdList: string[],
  year: number,
  month: number,
  date: number
): Promise<ResponseType<{ id?: string; meetingReservations: TReservation[] }[]>> => {
  try {
    const res = await instance.post("/meetings/meeting-reservation/list", {
      meetingRoomIdList: roomIdList,
      year: year,
      month: month,
      date: date,
    });

    return { success: true, data: res.data };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "회의실 예약 리스트를 가져올 수 있는 권한이 없습니다." };
    } else return { success: false, message: "회의실 예약 리스트를 가져올 수 없습니다." };
  }
};

//INFO: (new) 회의실 예약 상세 정보 가져오는 api
export const getMeetingReservationDetail = async (
  data: TGetMeetingReservation
): Promise<ResponseType<TReservation>> => {
  try {
    const res = await instance.post("/meetings/meeting-reservation/detail", {
      googleEventId: data.googleEventId ?? "",
      meetingReservationId: data.meetingReservationId ?? "",
      meetingRoomId: data.meetingRoomId ?? "",
    });

    return { success: true, data: res.data };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "회의실 예약 내역을 가져올 수 있는 권한이 없습니다." };
    } else return { success: false, message: "회의실 예약 내역을 가져올 수 없습니다." };
  }
};

//INFO: (new) 예정된 나의 참석/예약 리스트 가져오는 api
export const getMyUpcomingMeetingReservationList = async (
  page: number,
  viewStartDate: Date,
  viewEndDate: Date,
  creatorOnly: boolean
): Promise<ResponseType<TMyReservationPagination>> => {
  try {
    const res = await instance.post("/meetings/meeting-reservation/my/upcoming", {
      nowPageNumber: page,
      viewStartDate: viewStartDate,
      viewEndDate: viewEndDate,
      creatorOnly: creatorOnly,
    });
    return { success: true, data: res.data };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "회의실 예약 리스트를 가져올 수 있는 권한이 없습니다." };
    } else return { success: false, message: "회의실 예약 리스트를 가져올 수 없습니다." };
  }
};

//INFO: (new) 예정된 나의 참석/예약 리스트 가져오는 api
export const getMyCompletedMeetingReservationList = async (
  page: number,
  viewStartDate: Date,
  viewEndDate: Date,
  creatorOnly: boolean
): Promise<ResponseType<TMyReservationPagination>> => {
  try {
    const res = await instance.post("/meetings/meeting-reservation/my/completed", {
      nowPageNumber: page,
      viewStartDate: viewStartDate,
      viewEndDate: viewEndDate,
      creatorOnly: creatorOnly,
    });
    return { success: true, data: res.data };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "회의실 예약 리스트를 가져올 수 있는 권한이 없습니다." };
    } else return { success: false, message: "회의실 예약 리스트를 가져올 수 없습니다." };
  }
};

//* INFO: (new) 회의실 예약 취소하는 api (redirect에서만 사용되는 api 입니다.)
export const cancelMeetingReservation = async (
  id: string,
  messageId: string,
  memberPublicId: string,
  messageLocation: "USAGE_CONFIRM_ALARM" | "NOSHOW_WARNING" | "REMINDER"
): Promise<{ success: boolean; status: number }> => {
  try {
    const res = await instance.delete("/meetings/meeting-reservation/by-usage-confirm-alarm", {
      data: {
        id: id,
        channel: "EMAIL",
        memberPublicId: memberPublicId,
        messageId: messageId,
        messageLocation: messageLocation,
      },
    });

    return { success: res.data.result, status: 200 };
  } catch (error: any) {
    return { success: false, status: error.response.status };
  }
};

type TCheckDuplicationReservation = {
  available: string[];
  unavailable: string[];
};

//* INFO: 반복 예약 중복 체크하는 api
export const checkDuplicationRecurringReservation = async ({
  rsvStartTime,
  rsvEndTime,
  meetingRoomId,
  rrule,
  editOption,
  instanceId,
}: {
  rsvStartTime: string;
  rsvEndTime: string;
  meetingRoomId: string;
  rrule: string;
  editOption?: RepeatReservationConditionalOption;
  instanceId?: string;
}) => {
  let option =
    editOption === RepeatReservationConditionalOption.ALL_SCHEDULES
      ? "ALL"
      : editOption === RepeatReservationConditionalOption.THIS_SCHEDULE
        ? "THIS"
        : editOption === RepeatReservationConditionalOption.THIS_SCHEDULE_AND_FUTURE_SCHEDULES
          ? "THIS_AND_FUTURE"
          : undefined;

  const data = {
    rsvStartTime,
    rsvEndTime,
    meetingRoomId,
    rrule,
    editOption: option,
    instanceId,
  };

  try {
    const res = await instance.post("/meetings/recurring-reservation/check-duplication", data);
    return { success: true, data: res.data as TCheckDuplicationReservation };
  } catch (error: any) {
    return { success: false, message: "반복 예약 중복을 확인할 수 없습니다." };
  }
};
